/**
 * #580 live updates, unit 11 part 2 — which reviews the child's Today echo may announce, and when.
 *
 * The only input is `GET /api/completion-reviews`, the signed-in member's OWN reviews. An event only makes
 * Today read it again; no payload value gets here.
 *
 * **What is announced.** A transition this tab SAW: a completion it knew as `pending` that the read now
 * reports `approved` or `held`. A completion first seen already decided is current state, not news. So:
 *
 *   - so the first read for a member is a SILENT BASELINE: nothing was known pending yet, so nothing it
 *     reports as decided is news (nothing replays at cold load or after a reload);
 *   - a completion the child just lit is noted pending at once (`notePending`), so a fast parent is not
 *     missed merely because no read landed in between;
 *   - a read during an Undo window still notes what it reports pending (`notePendingRows`); its decisions wait
 *     for the deadline read, so a completion first seen pending in that window is not lost;
 *   - a missed event can be recovered by the next read (rejoin, visible tab, the 30-second interval) while
 *     its decision is within the read's seven-day window and returned page. Known-ID reads of any age are
 *     not implemented here. Pending observations absent for seven days expire on the next reconciliation.
 *
 * **How "already shown" is remembered** — the smallest way the design allows (§4): in memory, in the
 * household realtime provider, one store per signed-in authority. It survives leaving Today and coming back
 * (an approval made meanwhile shows once on return) and is dropped on sign-out, a new sign-in or a reload.
 * Nothing is written to storage or to the server; exactly-once across reloads or browsers is not promised.
 * Once a decision is announced its completion is recorded as decided, and a decision never goes back to
 * pending, so the same completion cannot be announced twice.
 *
 * ponytail: in-memory per authority, bounded to MAX_ENTRIES; sessionStorage if echoes must survive a reload.
 */

export const MAX_ENTRIES = 500;
export const RECOVERY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const STATES = new Set(['pending', 'approved', 'held']);

export function createReviewMemory() {
  return { subjectId: null, known: new Map(), pendingAt: new Map() };
}

function bound(memory) {
  while (memory.known.size > MAX_ENTRIES) {
    const first = memory.known.keys().next().value;
    memory.known.delete(first);
    memory.pendingAt.delete(first);
  }
}

/** The child just lit this completion and the server said a parent will look at it. */
export function notePending(memory, subjectId, completionId) {
  if (!memory || !Number.isInteger(completionId) || !Number.isInteger(subjectId)) return;
  if (memory.subjectId !== subjectId) {
    // No read for this member has landed yet (or the tab now shows somebody else). Keep the note for the read
    // that will: it starts this member's memory exactly as that read would, so cold-load silence still holds.
    memory.subjectId = subjectId;
    memory.known.clear();
    memory.pendingAt.clear();
  }
  if (!memory.known.has(completionId)) {
    memory.known.set(completionId, 'pending');
    memory.pendingAt.set(completionId, Date.now());
  }
  bound(memory);
}

/**
 * A read that landed while an Undo window is open: note what it reports pending, so a decision read later is a
 * transition this tab saw. Its decided rows are NOT folded and nothing is announced: they wait for the deadline
 * read (`reconcileReviews`). A completion already known is left as it is, so a decision never goes back to pending.
 */
export function notePendingRows(memory, answer) {
  if (!memory || !answer) return;
  if (memory.subjectId !== answer.subjectId) {
    // A different member on this tab: forget everything about the last one, as `reconcileReviews` does.
    memory.subjectId = answer.subjectId;
    memory.known.clear();
    memory.pendingAt.clear();
  }
  for (const r of answer.reviews) {
    if (r.state === 'pending') notePending(memory, answer.subjectId, r.completionId);
  }
}

const isTime = (v) => typeof v === 'string' && !Number.isNaN(Date.parse(v));

/**
 * A strict reading of the answer FOR THIS MEMBER, or `null`. An answer about anybody else, or of a shape this
 * client does not know, is not read at all — it can never become an echo.
 */
export function toReviewAnswer(raw, subjectId) {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.reviews)) return null;
  if (!Number.isInteger(subjectId) || raw.subjectUserId !== subjectId) return null;
  const reviews = [];
  for (const r of raw.reviews) {
    if (!r || typeof r !== 'object') return null;
    if (!Number.isInteger(r.completionId) || !Number.isInteger(r.approvalId)) return null;
    if (!STATES.has(r.state) || typeof r.questTitle !== 'string' || !r.questTitle.trim()) return null;
    if (r.state !== 'pending' && !isTime(r.decidedAt)) return null;
    reviews.push({
      completionId: r.completionId,
      approvalId: r.approvalId,
      state: r.state,
      questTitle: r.questTitle.trim(),
      decidedAt: r.decidedAt ?? null,
      reason: r.state === 'held' && typeof r.reason === 'string' && r.reason.trim() ? r.reason.trim() : null,
    });
  }
  return { subjectId, reviews };
}

/**
 * Fold one read into the memory and return what is NEWLY decided since this tab knew it pending.
 * @returns {{ approved: object[], held: object[] }}
 */
export function reconcileReviews(memory, answer) {
  const found = { approved: [], held: [] };
  if (!memory || !answer) return found;
  if (memory.subjectId !== answer.subjectId) {
    // A different member on this tab: forget everything about the last one.
    memory.subjectId = answer.subjectId;
    memory.known.clear();
    memory.pendingAt.clear();
  }
  const now = Date.now();
  // A newest-first truncated page says nothing about omitted IDs: only returned rows can cause an echo.
  for (const r of answer.reviews) {
    const before = memory.known.get(r.completionId);
    memory.known.delete(r.completionId); // re-insert: most recent last, so the bound drops the oldest
    memory.known.set(r.completionId, r.state);
    if (r.state === 'pending') memory.pendingAt.set(r.completionId, now);
    else memory.pendingAt.delete(r.completionId);
    if (r.state === 'pending' || before !== 'pending') continue;
    found[r.state].push(r);
  }
  for (const [id, observedAt] of memory.pendingAt) {
    if (now - observedAt > RECOVERY_WINDOW_MS) {
      memory.known.delete(id);
      memory.pendingAt.delete(id);
    }
  }
  bound(memory);
  return found;
}
